import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Activity, Users, TrendingUp, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AbsencesReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAbsencesData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/reports/absences`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching absences:', error);
      setError('Erro ao carregar dados de afastamentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAbsencesData();
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      fetchAbsencesData();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="absences-loading">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12" data-testid="absences-error">
        <p className="text-red-500">{error}</p>
        <button 
          onClick={fetchAbsencesData} 
          className="mt-4 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  // Default values if data is null or missing fields
  const safeData = {
    total_afastamentos: data?.total_afastamentos || 0,
    total_cids: data?.total_cids || 0,
    ranking_por_cid: data?.ranking_por_cid || []
  };

  return (
    <div className="space-y-6" data-testid="absences-report-page">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Relatório de Afastamentos por CID
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Análise de afastamentos e classificações médicas
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-100 dark:bg-yellow-900 p-3 rounded-lg">
              <Activity size={24} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Total Afastamentos</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {safeData.total_afastamentos}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
              <TrendingUp size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">CIDs Diferentes</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {safeData.total_cids}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-lg">
              <Users size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Média por CID</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {safeData.total_cids > 0 ? (safeData.total_afastamentos / safeData.total_cids).toFixed(1) : 0}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Chart */}
      {safeData.ranking_por_cid.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Ranking de CIDs - Mais Frequentes
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={safeData.ranking_por_cid.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="cid" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="total" fill="#EAB308" name="Afastamentos" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Detailed List */}
      {safeData.ranking_por_cid.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Detalhamento por CID
          </h3>
          {safeData.ranking_por_cid.map((cid, index) => (
            <Card key={index} className="p-6" data-testid={`cid-${cid.cid}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-xl font-bold text-slate-900 dark:text-white">
                      {cid.cid}
                    </h4>
                    <Badge className="bg-yellow-500">
                      {cid.total} {cid.total === 1 ? 'afastamento' : 'afastamentos'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Funcionários afastados com este CID */}
              <div className="space-y-3">
                {cid.funcionarios?.map((func, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h5 className="font-semibold text-slate-900 dark:text-white">
                          {func.nome}
                        </h5>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {func.cargo} - {func.setor}
                        </p>
                      </div>
                      <div className="text-right text-sm">
                        {func.data_saida && (
                          <p className="text-slate-600">
                            <span className="font-semibold">Saída:</span> {func.data_saida}
                          </p>
                        )}
                        {func.data_retorno && (
                          <p className="text-emerald-600">
                            <span className="font-semibold">Retorno:</span> {func.data_retorno}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {safeData.total_afastamentos === 0 && (
        <Card className="p-12 text-center">
          <Activity size={48} className="mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 dark:text-slate-400">
            Nenhum afastamento registrado no momento.
          </p>
        </Card>
      )}

      {/* Info Card */}
      <Card className="p-6 bg-blue-50 dark:bg-blue-950">
        <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2">
          Sobre a CID (Classificação Internacional de Doenças)
        </h4>
        <p className="text-sm text-blue-800 dark:text-blue-200">
          A CID é utilizada para identificar e classificar doenças e outros problemas de saúde.
          Este relatório ajuda a empresa a identificar padrões de afastamentos e implementar
          medidas preventivas de saúde ocupacional.
        </p>
      </Card>
    </div>
  );
};

export default AbsencesReport;
